import { useAuth0 } from "@auth0/auth0-react";
import { Grid, Select, Input, Button, Spin, Message, Checkbox } from "@arco-design/web-react";
import React, { useEffect, useMemo } from 'react';
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { FetchApi, SaveApi } from "../../Api/AuthenticatedApi";
import { formEqual } from "../../utils/formEqual";
import { useUnsavedGuard } from "../../utils/useUnsavedGuard";
import Content from "../../Layout/Content";
import { setOrgSettings } from "../../utils/useOrgFeatures";

const { Row, Col } = Grid;
const Option = Select.Option;

// Always-editable settings form: fields are live, Save enables only when
// something changed vs. the loaded record, and Discard reverts while dirty.

const FormControl = styled.div`
  margin: 8px;
  min-width: 200px;
  display: inline-flex;
  flex-direction: column;
`;

const FieldLabel = styled.div`
  font-size: 12px;
  color: var(--color-text-3);
  margin-bottom: 4px;
`;

function valuesFromRecord(r) {
    return {
        orgName: "" + (r && r.name != null ? r.name : ""),
        orgHash: "" + (r && r.org_hash != null ? r.org_hash : ""),
        unsubscribeUrl: (r && r.settings && r.settings.unsubscribe_url) || "",
        unsubscribeAll: !!(r && r.settings && r.settings.unsubscribe_all === "true"),
        companies: !!(r && r.settings && r.settings.enable_contact_companies === "true"),
        workflows: !!(r && r.settings && r.settings.enable_workflows === "true"),
    };
}

export default function OrganizationSettings() {
    const [orgName, setOrgName] = React.useState("");
    const [orgHash, setOrgHash] = React.useState("");
    const [selectedOrgHash, setSelectedOrgHash] = React.useState("")
    const [unsubscribeUrl, setUnsubscribeUrl] = React.useState("");
    const [unsubscribeAll, setUnsubscribeAll] = React.useState(false);
    const [companies, setCompanies] = React.useState(false);
    const [workflows, setWorkflows] = React.useState(false);

    const { getAccessTokenSilently } = useAuth0();
    const [record, setRecord] = React.useState({});
    const [loading, setLoading] = React.useState(true);



    let history = useNavigate();

    useEffect(() => {
        (async () => {
            let url = "/api/settings/organization"
            FetchApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    setRecord(data);
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to load organization settings");
                })
        })();
    }, [setRecord, setLoading, history, getAccessTokenSilently])

    function saveSettings(data) {
        (async () => {
            let url = "/api/settings/organization/settings"
            SaveApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    setRecord(data);
                    // The sidebar and other screens follow the new settings.
                    setOrgSettings(data && data.settings);
                    Message.success("Organization settings saved");
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to save organization settings");
                }, data);
        })();
    }

    const save = () => {
        saveSettings({
            name: orgName,
            org_hash: selectedOrgHash,
            // Preserve any settings this form doesn't edit (e.g. the sequence
            // send-window) — the backend replaces the whole settings map.
            settings: {
                ...(record.settings || {}),
                unsubscribe_url: unsubscribeUrl,
                unsubscribe_all: unsubscribeAll ? "true" : "false",
                enable_contact_companies: companies ? "true" : "false",
                enable_workflows: workflows ? "true" : "false",
            },
        });
    }

    const setFromRecord = (r) => {
        setOrgName("" + r.name);
        setOrgHash("" + r.org_hash);
        setSelectedOrgHash("" + r.org_hash);
        setUnsubscribeUrl(r.settings?.unsubscribe_url || "")
        setUnsubscribeAll(r.settings?.unsubscribe_all === "true")
        setCompanies(r.settings?.enable_contact_companies === "true")
        setWorkflows(r.settings?.enable_workflows === "true")
    }

    const discard = () => setFromRecord(record);

    const handleUserChangeOrgHash = (value) => {
        setSelectedOrgHash(value);
    }

    React.useEffect(() => {
        setFromRecord(record)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record])

    const baseline = useMemo(() => valuesFromRecord(record), [record]);
    const current = {
        orgName,
        orgHash: selectedOrgHash,
        unsubscribeUrl,
        unsubscribeAll,
        companies,
        workflows,
    };
    const isDirty = !formEqual(current, baseline);
    useUnsavedGuard(isDirty);


    if (loading) {
        return (
            <Content>
                <Spin />
            </Content>
        );
    }

    return (
        <Row>
            <Col span={24}>
                <FormControl style={{ width: "100%" }}>
                    <FieldLabel>Name</FieldLabel>
                    <Input
                        id="name"
                        value={orgName}
                        onChange={(v) => setOrgName(v)}
                    />
                </FormControl>
            </Col>

            <Col span={24}>
                <FormControl style={{ width: "100%" }}>
                    <FieldLabel>Goodbye/unsubscribe URL</FieldLabel>
                    <Input
                        id="unsubscribeUrl"
                        value={unsubscribeUrl}
                        onChange={(v) => setUnsubscribeUrl(v)}
                    />
                </FormControl>
            </Col>

            <Col span={24}>
                <FormControl style={{ width: "100%" }}>
                    <Checkbox
                        checked={unsubscribeAll}
                        onChange={setUnsubscribeAll}
                    >
                        Unsubscribing from one email unsubscribes the contact from all lists
                    </Checkbox>
                    <div style={{ fontSize: 12, color: "var(--color-text-3)", marginTop: 4 }}>
                        Off = unsubscribe only from the list that email belonged to.
                    </div>
                </FormControl>
            </Col>

            <Col span={24}>
                <FormControl style={{ width: "100%" }}>
                    <FieldLabel>Features</FieldLabel>
                    <Checkbox checked={companies} onChange={setCompanies}>
                        Companies
                    </Checkbox>
                    <div style={{ fontSize: 12, color: "var(--color-text-3)", margin: "4px 0 8px" }}>
                        Group contacts by the company they work for.
                    </div>
                    <Checkbox checked={workflows} onChange={setWorkflows}>
                        Workflows
                    </Checkbox>
                    <div style={{ fontSize: 12, color: "var(--color-text-3)", marginTop: 4 }}>
                        Move contacts through pipeline stages (Lead → Qualified → Won).
                        Turning a feature off only hides it; nothing is deleted.
                    </div>
                </FormControl>
            </Col>

            <Col span={24}>
                <FormControl>
                    <FieldLabel>Organization API Id</FieldLabel>
                    <Select
                        id="select-org-hash-id"
                        value={selectedOrgHash}
                        onChange={handleUserChangeOrgHash}
                    >
                        <Option value={orgHash}>{orgHash}</Option>
                        <Option value={"REFRESH"}>{"Refresh"}</Option>
                    </Select>
                </FormControl>
            </Col>

            <Col span={24}>
                &nbsp;
            </Col>


            <Col span={24} style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 8 }}>
                <Button type="primary" onClick={save} disabled={!isDirty}>Save</Button>
                {isDirty && <Button onClick={discard}>Discard</Button>}
            </Col>

        </Row>

    );
}
